import { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { TOTAL_QUESTIONS, questions, consentItems, questionSteps } from '@/mocks/surveyData';

const STORAGE_KEY = 'survey_draft';

// 👇 아래 URL을 실제 웹훅 엔드포인트로 교체하세요 (예: Make, Zapier, 사내 서버 등)
const API_URL = 'https://accuz.eemo.co.kr/api/survey.php';

export interface SurveyState {
  currentStep: number;
  answers: Record<string, unknown>;
  isSubmitting: boolean;
  submitError: string;
  progress: number;
  answeredCount: number;
  cid: string | null;
  shop: string | null;
  goNext: () => void;
  goPrev: () => void;
  submit: () => Promise<void>;
  updateAnswer: (questionId: string, value: unknown) => void;
  updateConsent: (consentId: string, checked: boolean) => void;
  unansweredRequiredIds: string[];
  setUnansweredRequiredIds: (ids: string[]) => void;
}

export function useSurvey(): SurveyState {
  const [searchParams] = useSearchParams();
  const cid = searchParams.get('cid');
  const shop = searchParams.get('shop');

  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, unknown>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [unansweredRequiredIds, setUnansweredRequiredIds] = useState<string[]>([]);

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed.answers) setAnswers(parsed.answers);
        if (typeof parsed.currentStep === 'number' && parsed.currentStep !== 16) {
          setCurrentStep(parsed.currentStep);
        }
      } catch {
        // corrupted data, ignore
      }
    }
  }, []);

  useEffect(() => {
    if (currentStep !== 16) {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ answers, currentStep }),
      );
    }
  }, [answers, currentStep]);

  const answeredCount = useMemo(() => {
    let count = 0;
    Object.keys(questions).forEach((qId) => {
      const answer = answers[qId];
      if (answer === undefined || answer === null || answer === '') return;
      if (Array.isArray(answer) && answer.length === 0) return;
      count += 1;
    });
    return count;
  }, [answers]);

  const progress = useMemo(
    () => Math.round((answeredCount / TOTAL_QUESTIONS) * 100),
    [answeredCount],
  );

  const getUnansweredRequiredIds = useCallback(() => {
    if (currentStep >= 1 && currentStep <= 14) {
      const stepData = questionSteps[currentStep - 1];
      const missing: string[] = [];
      stepData.questionIds.forEach((qId) => {
        const q = questions[qId];
        if (!q || !q.required) return;
        const answer = answers[qId];
        if (answer === undefined || answer === null || answer === '') {
          missing.push(qId);
          return;
        }
        if (Array.isArray(answer) && answer.length === 0) {
          missing.push(qId);
        }
      });
      return missing;
    }
    return [];
  }, [currentStep, answers]);

  const updateAnswer = useCallback((questionId: string, value: unknown) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
    setUnansweredRequiredIds((prev) => prev.filter((id) => id !== questionId));
  }, []);

  const updateConsent = useCallback((consentId: string, checked: boolean) => {
    setAnswers((prev) => ({ ...prev, [consentId]: checked }));
  }, []);

  const goNext = useCallback(() => {
    if (currentStep === 16) return;
    const missing = getUnansweredRequiredIds();
    if (missing.length > 0) {
      setUnansweredRequiredIds(missing);
      return;
    }
    setUnansweredRequiredIds([]);
    setCurrentStep((prev) => prev + 1);
  }, [currentStep, getUnansweredRequiredIds]);

  const goPrev = useCallback(() => {
    if (currentStep === 0) return;
    setUnansweredRequiredIds([]);
    setCurrentStep((prev) => prev - 1);
  }, [currentStep]);

  const buildPayload = useCallback(() => {
    const answersPayload: Record<string, unknown> = {};
    Object.keys(questions).forEach((qId) => {
      const key = qId.toLowerCase();
      answersPayload[key] = answers[qId] ?? null;
    });

    const consentPayload: Record<string, boolean> = {};
    consentItems.forEach((item) => {
      consentPayload[item.key] = answers[item.id] === true;
    });

    return {
      cid,
      shop,
      submittedAt: new Date().toISOString(),
      answers: answersPayload,
      consent: consentPayload,
    };
  }, [answers, cid, shop]);

  const submit = useCallback(async () => {
    setIsSubmitting(true);
    setSubmitError('');
    try {
      const payload = buildPayload();
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      let data: { ok?: boolean; message?: string; error?: string } = {};
      try {
        data = await response.json();
      } catch {
        // 응답 본문을 파싱할 수 없으면 빈 객체 유지
      }

      if (!response.ok || !data.ok) {
        let serverMsg = `서버 오류 (HTTP ${response.status}). 잠시 후 다시 시도해주세요.`;
        if (data.message || data.error) {
          serverMsg = data.message || data.error || serverMsg;
        }
        throw new Error(serverMsg);
      }

      localStorage.removeItem(STORAGE_KEY);
      setCurrentStep(16);
    } catch (e) {
      const message =
        e instanceof Error ? e.message : '제출 중 오류가 발생했어요. 다시 시도해주세요.';
      setSubmitError(message);
    } finally {
      setIsSubmitting(false);
    }
  }, [buildPayload]);

  return {
    currentStep,
    answers,
    isSubmitting,
    submitError,
    progress,
    answeredCount,
    cid,
    shop,
    goNext,
    goPrev,
    submit,
    updateAnswer,
    updateConsent,
    unansweredRequiredIds,
    setUnansweredRequiredIds,
  };
}